import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
  IconBrain,
  IconSparkles,
  IconRobot,
  IconCheck,
  IconDeviceFloppy,
  IconArrowLeft,
  IconArrowRight,
  IconX,
  IconMoodSad,
  IconBriefcase,
  IconClock,
  IconTag,
} from "@tabler/icons-react";
import classes from "./testRedesign.module.css";
import {
  getTalentCurrentTest,
  getTalentTestAnswers,
  submitTalentCurrentTest,
  talentProfile,
} from "../../../services/talentCalls";
import { errorHandler, toastSuccess } from "../../../utils";
import { useUserContext } from "../../../context";
import useCountdown from "../../../hooks/useCountdown";
import { formatPsychometricAnswers } from "../../../utils/functions";

const MAX_CHARACTERS = 700;
const TIMER_DURATION = 30 * 60;

const WELCOME_SEEN_KEY = "startModal";
const CONSENT_KEY = "testConsentGiven";

const SCENARIO_TAGS = [
  "Prioritisation", "Stakeholder conflict", "Ambiguity", "Communication", "Judgement",
  "Data quality", "Influence", "Trade-offs", "Pressure", "Ethics",
];

const formatClock = (secs) => {
  const m = Math.floor(Math.max(0, secs) / 60);
  const s = Math.max(0, secs) % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const Test = () => {
  const navigate = useNavigate();
  const { data, setData } = useUserContext();

  const [test, setTest] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stage, setStage] = useState(1);
  const [answers, setAnswers] = useState({});
  const [testCompleted, setTestCompleted] = useState(false);

  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [consentOpen, setConsentOpen] = useState(false);
  const [profileSettled, setProfileSettled] = useState(false);
  const hasOpenedIntroModals = useRef(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [clipboardNotice, setClipboardNotice] = useState("");

  const debounceRef = useRef(null);
  const textareaRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const questionIdRef = useRef(null);
  const integrityRef = useRef({
    pastedCharacters: 0,
    pastedQuestionIds: [],
    tabBlurCount: 0,
    tabBlurDurationMs: 0,
    keystrokeCount: 0,
    itemLatenciesMs: {},
    itemStartedAt: Date.now(),
  });

  const hasSubmittedTest = useMemo(() => data?.submittedPsychometricTest === true, [data]);

  // The intro modals are gated on a localStorage flag, which only tracks "seen on
  // this device" — it doesn't know the test was already completed (e.g. on another
  // device, or after clearing storage). Opening them straight from that flag meant
  // talents who had already finished the test saw a modal until the profile arrived
  // and closed it again, which was obvious when arriving from an email link. So wait
  // for the profile, then open at most one of them. If the fetch fails we still open,
  // rather than stranding a first-time talent with no way into the test.
  useEffect(() => {
    if (hasOpenedIntroModals.current) return;
    if (!profileSettled || hasSubmittedTest) return;
    hasOpenedIntroModals.current = true;

    const welcomeSeen = !!localStorage.getItem(WELCOME_SEEN_KEY);
    const consented = !!localStorage.getItem(CONSENT_KEY);

    if (!welcomeSeen) setWelcomeOpen(true);
    else if (!consented) setConsentOpen(true);
  }, [profileSettled, hasSubmittedTest]);

  useEffect(() => {
    if (hasSubmittedTest) {
      setWelcomeOpen(false);
      setConsentOpen(false);
    }
  }, [hasSubmittedTest]);

  const psychometricTestTimes = useMemo(() => data?.psychometricTestTimes, [data]);
  const remainingTime = useMemo(
    () => TIMER_DURATION - Math.floor((psychometricTestTimes?.totalTimeSpent || 0) / 1000),
    [psychometricTestTimes],
  );

  const { timeLeft, clearTimer, countEnded } = useCountdown({
    count: remainingTime,
    startCountdown: !testCompleted && test?.questions?.length > 0 && !!psychometricTestTimes,
    id: data?.id,
  });

  const currentQuestion = test?.questions?.[stage - 1];
  questionIdRef.current = currentQuestion?.id;
  const total = test?.questions?.length || 0;
  const isFirst = stage === 1;
  const isLast = stage === total;

  const getCurrentTest = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getTalentCurrentTest();
      const profileRes = await talentProfile();
      const answersRes = await getTalentTestAnswers();

      if (profileRes?.data?.data?.talent) setData(profileRes?.data?.data?.talent);
      if (res?.data?.data?.psychometric_test) setTest(res?.data?.data?.psychometric_test);

      if (answersRes?.data?.data?.psychometricAnswers?.answers) {
        const savedAnswers = answersRes?.data?.data?.psychometricAnswers?.answers;
        setAnswers((prev) => ({ ...prev, ...savedAnswers }));

        const questions = res?.data?.data?.psychometric_test?.questions;
        if (questions) {
          let lastAnsweredIndex = -1;
          questions.forEach((q, i) => {
            if (savedAnswers[q.id]) lastAnsweredIndex = i;
          });
          if (lastAnsweredIndex !== -1) setStage(Math.min(lastAnsweredIndex + 2, questions.length));
        }
      }
    } catch (error) {
      errorHandler(error?.response?.data?.errors, "Error");
    } finally {
      setLoading(false);
      setProfileSettled(true);
    }
  }, [setData]);

  useEffect(() => {
    getCurrentTest();
  }, [getCurrentTest]);

  useEffect(() => {
    if (hasSubmittedTest) {
      setTestCompleted(true);
      clearTimer();
    }
  }, [hasSubmittedTest, clearTimer]);

  const persistAnswers = useCallback(
    async (action = "save") => {
      if (!test || !data) return;
      try {
        const formattedAnswers = formatPsychometricAnswers(test, answers);
        const payload = {
          formattedAnswers,
          answers,
          talentId: test?.talentId || data?._id,
          role: data?.pryRole,
          psychometricTestId: test?._id,
          integrity: {
            pastedCharacters: integrityRef.current.pastedCharacters,
            pastedQuestionIds: [...new Set(integrityRef.current.pastedQuestionIds)],
            tabBlurCount: integrityRef.current.tabBlurCount,
            tabBlurDurationMs: integrityRef.current.tabBlurDurationMs,
            keystrokeCount: integrityRef.current.keystrokeCount,
            itemLatencyMs: Object.values(integrityRef.current.itemLatenciesMs),
          },
        };
        const res = await submitTalentCurrentTest(payload, action);
        return res;
      } catch (error) {
        errorHandler(error?.response?.data?.errors, "Error");
      }
    },
    [test, data, answers],
  );

  const handleAutosave = useCallback(() => {
    if (hasSubmittedTest) return;
    persistAnswers("save");
  }, [persistAnswers, hasSubmittedTest]);

  const handleAnswerChange = (value) => {
    integrityRef.current.keystrokeCount += 1;
    setAnswers((prev) => ({ ...prev, [currentQuestion?.id]: value }));
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(handleAutosave, 700);
  };

  const blockClipboard = !testCompleted && (test?.questions?.length || 0) > 0;

  useEffect(() => {
    if (!blockClipboard) return undefined;

    const showNotice = (message) => {
      setClipboardNotice(message);
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
      noticeTimerRef.current = setTimeout(() => setClipboardNotice(""), 3200);
    };

    const recordPasteAttempt = (text) => {
      integrityRef.current.pastedCharacters += (text || "").length;
      const questionId = questionIdRef.current;
      if (questionId) integrityRef.current.pastedQuestionIds.push(String(questionId));
    };

    const onPaste = (event) => {
      event.preventDefault();
      recordPasteAttempt(event.clipboardData?.getData("text") || "");
      showNotice("Pasting is not allowed. Please type your answer.");
    };
    const onCopyOrCut = (event) => {
      event.preventDefault();
      showNotice("Copying is not allowed during this test.");
    };
    const onDragOver = (event) => {
      const types = event.dataTransfer?.types ? Array.from(event.dataTransfer.types) : [];
      if (types.includes("text/plain") || types.includes("text/html")) event.preventDefault();
    };
    const onDrop = (event) => {
      const text = event.dataTransfer?.getData("text") || "";
      if (!text) return;
      event.preventDefault();
      recordPasteAttempt(text);
      showNotice("Pasting is not allowed. Please type your answer.");
    };
    const onContextMenu = (event) => {
      event.preventDefault();
    };

    document.addEventListener("paste", onPaste, true);
    document.addEventListener("copy", onCopyOrCut, true);
    document.addEventListener("cut", onCopyOrCut, true);
    document.addEventListener("dragover", onDragOver, true);
    document.addEventListener("drop", onDrop, true);
    document.addEventListener("contextmenu", onContextMenu, true);
    return () => {
      document.removeEventListener("paste", onPaste, true);
      document.removeEventListener("copy", onCopyOrCut, true);
      document.removeEventListener("cut", onCopyOrCut, true);
      document.removeEventListener("dragover", onDragOver, true);
      document.removeEventListener("drop", onDrop, true);
      document.removeEventListener("contextmenu", onContextMenu, true);
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    };
  }, [blockClipboard]);

  useEffect(() => {
    const onBlur = () => {
      integrityRef.current.tabBlurCount += 1;
      integrityRef.current._blurStarted = Date.now();
    };
    const onFocus = () => {
      if (integrityRef.current._blurStarted) {
        integrityRef.current.tabBlurDurationMs += Date.now() - integrityRef.current._blurStarted;
        integrityRef.current._blurStarted = 0;
      }
    };
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    return () => {
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
    };
  }, []);

  const handleFinish = useCallback(async () => {
    if (hasSubmittedTest) return;
    setLoading(true);
    const res = await persistAnswers("submit");
    setLoading(false);
    if (res?.data?.success) {
      toastSuccess("Test submitted successfully");
      setTestCompleted(true);
      const profileRes = await talentProfile();
      if (profileRes?.data?.data?.talent) setData(profileRes.data.data.talent);
    }
  }, [persistAnswers, hasSubmittedTest, setData]);

  const handleNext = () => {
    clearTimeout(debounceRef.current);
    if (currentQuestion?.id) {
      const started = integrityRef.current.itemStartedAt || Date.now();
      integrityRef.current.itemLatenciesMs[currentQuestion.id] = Date.now() - started;
    }
    integrityRef.current.itemStartedAt = Date.now();
    if (isLast) {
      handleFinish();
    } else {
      setStage((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    clearTimeout(debounceRef.current);
    setStage((prev) => Math.max(1, prev - 1));
  };

  useEffect(() => {
    if (countEnded && !testCompleted && test?.questions?.length > 0) {
      handleFinish();
    }
  }, [countEnded, testCompleted, test, handleFinish]);

  useEffect(() => {
    if (window.innerWidth > 768 && textareaRef.current && !testCompleted) {
      textareaRef.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const closeWelcome = () => {
    localStorage.setItem(WELCOME_SEEN_KEY, "true");
    setWelcomeOpen(false);
    if (localStorage.getItem(CONSENT_KEY)) return;
    setConsentOpen(true);
  };

  const proceedConsent = () => {
    localStorage.setItem(CONSENT_KEY, "true");
    setConsentOpen(false);
  };

  const pct = total ? Math.round(((testCompleted ? total : stage) / total) * 100) : 0;
  const scenarioTag = SCENARIO_TAGS[(stage - 1) % SCENARIO_TAGS.length];

  return (
    <div className={classes.main}>
      <div className={classes.pageHeader}>
        <div className={classes.pageTitleRow}>
          <div>
            <span className={classes.eyebrow}>
              <IconBrain size={14} /> Psychometric Test
            </span>
            <h1 className={classes.pageTitle}>Show employers how you think under pressure.</h1>
            <p className={classes.pageSubtitle}>
              Answer a short set of scenario questions — your responses are shared with matching employers.
            </p>
          </div>
          <span className={classes.aiChip}>
            <IconSparkles size={14} /> Powered by AI
          </span>
        </div>
      </div>

      <div className={classes.card}>
        {!loading && !test?.questions?.length ? (
          <div className={classes.centerState}>
            <div className={classes.illustrationWrap}>
              <IconMoodSad size={48} />
            </div>
            <div className={classes.centerMessage}>Sorry, there are no tests available for this role</div>
            <button className={classes.btnAmber} onClick={() => navigate("/talent-report")}>
              Contact Support
            </button>
          </div>
        ) : testCompleted ? (
          <div className={classes.centerState}>
            <div className={`${classes.illustrationWrap} ${classes.illustrationWrapDone}`}>
              <IconCheck size={48} />
            </div>
            <div className={classes.centerMessage}>All done — thank you!</div>
            <button className={classes.btnAmber} onClick={() => navigate("/talent-dashboard")}>
              OK
            </button>
          </div>
        ) : (
          <>
            <div className={classes.qHeadRow}>
              <div className={classes.qTag}>
                <IconBriefcase size={13} /> {data?.pryRole}
              </div>
              {psychometricTestTimes && (
                <span className={`${classes.progressTimer} ${timeLeft <= 60 ? classes.progressTimerLow : ""}`}>
                  <IconClock size={12} /> {formatClock(timeLeft)}
                </span>
              )}
            </div>

            <div className={classes.progressWrap}>
              <div className={classes.progressMeta}>
                <span className={classes.progressCount}>
                  Question {stage} of {total}
                </span>
                <span className={classes.progressPct}>{pct}%</span>
              </div>
              <div className={classes.track}>
                <div className={classes.trackFill} style={{ width: `${pct}%` }} />
              </div>
            </div>

            <div className={classes.questionView}>
              <div className={classes.qTag}>
                <IconTag size={13} /> {scenarioTag}
              </div>
              <div className={classes.qSender}>
                <span className={classes.qAvatar}>
                  <IconRobot size={20} />
                </span>
                <div>
                  <span className={classes.qSenderName}>
                    Awasource AI <span className={classes.qBadge}><IconCheck size={9} /></span>
                  </span>
                  <div className={classes.qSenderSub}>Question {stage}</div>
                </div>
              </div>
              <div className={classes.qText}>{currentQuestion?.text}</div>

              <label className={classes.answerLabel} htmlFor="testAnswerInput">
                Your response
              </label>
              <p className={classes.pasteHint}>Type your answer. Copying and pasting is not allowed.</p>

              <textarea
                ref={textareaRef}
                id="testAnswerInput"
                className={classes.textarea}
                placeholder="Type your response here. Pasting is not allowed."
                maxLength={MAX_CHARACTERS}
                disabled={hasSubmittedTest}
                value={answers[currentQuestion?.id] || ""}
                onChange={(e) => handleAnswerChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleNext();
                  }
                }}
              />

              <div className={classes.actions}>
                <span className={classes.savedHint}>
                  <IconDeviceFloppy size={13} /> Saved automatically
                </span>
                <div className={classes.actionsRight}>
                  <span className={classes.enterHint}>
                    press <kbd>Enter ↵</kbd>
                  </span>
                  {!isFirst && (
                    <button className={classes.btnGhost} onClick={handleBack}>
                      <IconArrowLeft size={15} /> Back
                    </button>
                  )}
                  <button className={classes.btnAmber} disabled={hasSubmittedTest || !answers[currentQuestion?.id]} onClick={handleNext}>
                    {isLast ? (
                      <>Submit <IconCheck size={15} /></>
                    ) : (
                      <>Next <IconArrowRight size={15} /></>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Welcome modal ── */}
      {welcomeOpen && (
        <div className={classes.modalOverlay}>
          <div className={classes.modal}>
            <button className={classes.modalCloseBtn} onClick={closeWelcome} aria-label="Close">
              <IconX size={15} />
            </button>
            <div className={classes.modalContent}>
              <div className={classes.modalIllustrationWrap}>
                <IconBrain size={48} color="#fff" />
              </div>
              <h2 className={classes.modalTitle}>Welcome!</h2>
              <p className={classes.modalBody}>
                Hi, welcome to the Psychometric Test. This helps employers understand how you approach real
                situations on the job. Type your answers yourself — copying and pasting is not allowed.
                Our AI reviews your answers and shares the results with companies considering you for a role.
              </p>
              <button className={classes.btnAmber} onClick={closeWelcome}>
                <IconArrowRight size={15} /> Let's Begin
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Consent modal ── */}
      {consentOpen && (
        <div className={classes.modalOverlay}>
          <div className={classes.modal}>
            <button
              className={classes.modalCloseBtn}
              onClick={() => setConsentOpen(false)}
              aria-label="Close"
            >
              <IconX size={15} />
            </button>
            <div className={classes.modalContent}>
              <label className={classes.disclaimerRow}>
                <input
                  type="checkbox"
                  className={classes.checkboxInput}
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                />
                <span className={classes.checkboxBox}>{consentChecked && <IconCheck size={15} />}</span>
                <span className={classes.disclaimerText}>
                  I acknowledge that Awasource will process my profile, CV, and assessment responses,
                  including through automated and AI-assisted systems, to generate personalized AI Insights
                  in accordance with the Privacy Policy and I understand that these insights provide guidance
                  on my strengths, potential career paths, and opportunities to help me make informed
                  decisions. While individual outcomes may vary, the insights are designed to support my
                  professional growth.
                </span>
              </label>
              <button className={classes.btnAmber} disabled={!consentChecked} onClick={proceedConsent}>
                Proceed
              </button>
            </div>
          </div>
        </div>
      )}
      {clipboardNotice && <div className={classes.warnToast} role="status">{clipboardNotice}</div>}
    </div>
  );
};

export default Test;
